import React, { useContext, useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    CloseRounded,
    PersonRounded,
} from "@mui/icons-material";
import { corporatePersonnel_addPersonnelCredit } from "../../../network/api/corporatePersonnel.api";
import { sport_getAll } from "../../../network/api/sport.api";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { getCurrentPersianMonthName, getCurrentPersianSeasonName, toPriceWithComma, toPriceWithoutComma } from "../../../helper/utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CreditUsageCategoryEnum } from "../../../helper/enums/CreditUsageCategoryEnum";
import { getUnifiedSportsList } from "../../../helper/constants/sportsConstants";
import _CreditRestrictionsPicker from "./_CreditRestrictionsPicker";

const PRESET_NAMES = [
    `اعتبار ${getCurrentPersianMonthName()}`,
    `اعتبار ${getCurrentPersianSeasonName()}`,
    "هدیه مناسبتی",
    "بن رفاهی جیم‌پین",
];

const PRESET_AMOUNTS = [
    1000000,
    2000000,
    3000000,
    5000000,
];

const _UserAddCreditModal = ({ open, onClose, corporatePersonnel, corporate, onSuccess }) => {
    const error = useContext(ErrorContext);
    const [submitting, setSubmitting] = useState(false);

    // Form fields
    const [name, setName] = useState("اعتبار ورزشی ماهانه");
    const [creditAmount, setCreditAmount] = useState("1000000");
    const [expireDate, setExpireDate] = useState(() => {
        const days = corporate?.DefaultExpireDuration || 30;
        return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    });

    // Categories & Blocked Sports
    const [usageCategories, setUsageCategories] = useState(["SPORT"]);
    const [blockedSportIds, setBlockedSportIds] = useState([]);
    const [sportsList, setSportsList] = useState([]);
    const [loadingSports, setLoadingSports] = useState(false);

    useEffect(() => {
        if (open) {
            loadSports();
        }
    }, [open]);

    function loadSports() {
        if (sportsList.length > 0) return;
        setLoadingSports(true);
        sport_getAll()
            .then(res => {
                setSportsList(getUnifiedSportsList(res?.data?.Data || []));
            })
            .catch(() => setSportsList(getUnifiedSportsList([])))
            .finally(() => setLoadingSports(false));
    }

    const handleCategoryToggle = (catCode) => {
        const category = CreditUsageCategoryEnum[catCode];
        if (category && !category.active) return;
        if (usageCategories.includes(catCode)) {
            const next = usageCategories.filter(c => c !== catCode);
            setUsageCategories(next.length > 0 ? next : ["SPORT"]);
        } else {
            setUsageCategories([...usageCategories, catCode]);
        }
    };

    const handleAddCredit = (e) => {
        e.preventDefault();

        const amountNum = Number(creditAmount);
        if (!amountNum || amountNum <= 0) {
            error.showError({ message: "لطفاً مبلغ معتبری وارد نمایید" });
            return;
        }

        if (!name.trim()) {
            error.showError({ message: "لطفاً عنوان بسته اعتباری را وارد نمایید" });
            return;
        }

        if (corporate?.ContractType !== "ALPHA" && !expireDate) {
            error.showError({ message: "لطفاً تاریخ انقضای اعتبار را مشخص کنید" });
            return;
        }

        setSubmitting(true);
        const payload = {
            CorporatePersonnel: { Id: corporatePersonnel.Id },
            Name: name.trim(),
            CreditAmount: amountNum,
            ExpireDate: expireDate ? new Date(expireDate).toISOString() : null,
            UsageCategories: usageCategories.length > 0 ? usageCategories : ["SPORT"],
            BlockedSportIds: usageCategories.includes("SPORT") ? [...new Set(blockedSportIds)] : [],
        };

        corporatePersonnel_addPersonnelCredit(payload)
            .then(() => {
                setSubmitting(false);
                onClose();
                error.showError({ message: "اعتبار با موفقیت به کارمند اختصاص یافت" });
                if (onSuccess) onSuccess();
            })
            .catch(err => {
                setSubmitting(false);
                const msg = err?.response?.data?.Message || "خطا در تخصیص اعتبار به کارمند";
                error.showError({ message: msg });
            });
    };

    const isSportIncluded = usageCategories.includes("SPORT");
    const fullName = corporatePersonnel?.User?.FullName || corporatePersonnel?.PhoneNumber || "این کارمند";

    return (
        <Dialog
            open={open}
            onClose={() => !submitting && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.5,
                            bgcolor: 'rgba(28, 139, 156, 0.1)',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AddRounded />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            تخصیص اعتبار به کارمند
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            اعتبار با تعیین بخش مصرف و محدودیت ورزش‌ها
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={submitting}>
                    <CloseRounded fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Box component="form" onSubmit={handleAddCredit}>
                <DialogContent sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Target Employee Info */}
                    <Box
                        sx={{
                            p: 1.5,
                            px: 2,
                            borderRadius: 3,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}
                    >
                        <Avatar
                            src={corporatePersonnel?.User?.Avatar?.Url}
                            sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}
                        >
                            <PersonRounded fontSize="small" />
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {corporatePersonnel?.User?.FullName || "کارمند بدون نام"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', dir: 'ltr', display: 'block' }}>
                                {corporatePersonnel?.PhoneNumber || corporatePersonnel?.User?.PhoneNumber}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Title & Presets */}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                            عنوان بسته اعتباری
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.2 }}>
                            {PRESET_NAMES.map((preset) => (
                                <Chip
                                    key={preset}
                                    label={preset}
                                    size="small"
                                    onClick={() => setName(preset)}
                                    variant={name === preset ? "filled" : "outlined"}
                                    color={name === preset ? "primary" : "default"}
                                    sx={{ cursor: 'pointer', fontWeight: 600, borderRadius: 2 }}
                                />
                            ))}
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="مثلاً: اعتبار ورزشی آبان ماه"
                            required
                        />
                    </Box>

                    {/* Amount & Presets */}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                            مبلغ اعتبار (تومان)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.2 }}>
                            {PRESET_AMOUNTS.map((preset) => (
                                <Chip
                                    key={preset}
                                    label={`${toPriceWithComma(preset)} تومان`}
                                    size="small"
                                    onClick={() => setCreditAmount(String(preset))}
                                    variant={Number(creditAmount) === preset ? "filled" : "outlined"}
                                    color={Number(creditAmount) === preset ? "primary" : "default"}
                                    sx={{ cursor: 'pointer', fontWeight: 600, borderRadius: 2 }}
                                />
                            ))}
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            value={toPriceWithComma(creditAmount)}
                            onChange={(e) => setCreditAmount(toPriceWithoutComma(e.target.value))}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">تومان</InputAdornment>,
                            }}
                            required
                        />
                    </Box>

                    {/* Expire Date */}
                    {corporate?.ContractType !== "ALPHA" && (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                                تاریخ انقضای اعتبار
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
                                <DatePicker
                                    value={expireDate}
                                    onChange={(newDate) => setExpireDate(newDate)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            helperText: 'پس از این تاریخ، اعتبار باقی‌مانده کارمند منقضی خواهد شد.',
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    )}

                    <Divider />

                    <_CreditRestrictionsPicker
                        usageCategories={usageCategories}
                        handleCategoryToggle={handleCategoryToggle}
                        blockedSportIds={blockedSportIds}
                        setBlockedSportIds={setBlockedSportIds}
                        sportsList={sportsList}
                    />

                    {/* Confirmation Card */}
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 3,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.8
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">مخاطب:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{fullName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">مبلغ پرداختی:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                {toPriceWithComma(creditAmount)} تومان
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">بخش‌های مصرف:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {usageCategories.map(c => CreditUsageCategoryEnum[c]?.label || c).join('، ')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">محدودیت ورزشی:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: blockedSportIds.length > 0 ? 'error.main' : 'success.main' }}>
                                {blockedSportIds.length > 0 ? `${blockedSportIds.length} ورزش ممنوع` : "تمامی ورزش‌ها مجاز"}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                    <Button
                        variant="text"
                        onClick={onClose}
                        disabled={submitting}
                        sx={{ borderRadius: 2.5, px: 2.5, color: 'text.secondary' }}
                    >
                        انصراف
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={submitting}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            fontWeight: 700,
                            boxShadow: '0 4px 14px rgba(28, 139, 156, 0.3)',
                        }}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : "تأیید و تخصیص اعتبار"}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default _UserAddCreditModal;
